/**
 * Opportunity Calculator v2.3 — shared question bank.
 *
 * Branching question structure locked by Bryan 2026-05-07.
 *
 * ~40 main questions across 10 checkpoints, averaging 4 per CP with flex.
 * Follow-up questions fire conditionally based on parent answer point value.
 * Total bank: 40 main + 120 follow-ups = 160 questions.
 * Average user session: 40–70 questions (most follow-ups do not fire).
 *
 * Upgrade from v2.2 (22 main, no branching) to v2.3 (40 main + branching).
 *
 * Manufacturing-only. M-PACT with hyphen. No emojis. No exclamation points.
 * Roles: bdr/ae/manager/executive/marketing/se. Never "rep".
 *
 * SCORING MODEL:
 *   5 — Strong yes / confirmed / fully in place    → 4 points
 *   4 — Mostly yes / likely / mostly in place      → 3 points
 *   3 — Partial / mixed signals / unclear          → 2 points
 *   2 — Mostly no / weak / mostly missing          → 1 point
 *   1 — Hard no / blocked / absent                 → 0 points
 *   Skip                                           → null (excluded from denominator)
 *
 * composite_0_100 = round( (sum_points_answered / (4 × count_answered)) × 100 )
 * Confidence: high = ≥80% of MAIN questions answered, medium = 50–79%, low = <50%
 * (Confidence based on main-question completion rate only.)
 * RAG: 70+ green, 50–69 amber, <50 red
 *
 * BRANCHING:
 *   Follow-up rules live in FOLLOW_UP_RULES[].
 *   Each rule: { parentId, triggerWhen: { points: number[] }, ask: string[] }
 *   After each answer, evaluate rules where parentId === answered question id
 *   and the answer's point value is in triggerWhen.points.
 *   Matched follow-ups are inserted immediately after the parent.
 *   Skipped parent → NO follow-ups fire.
 *   Quick Score → NO follow-ups fire (flat 7 questions only).
 *
 * QUICK SCORE (7 questions, no branching):
 *   B1 — Budget
 *   A1 — Authority
 *   N1 — Need
 *   T1 — Timeline
 *   C1 — Competitor
 *   P1 — People Network + Coverage
 *   R1 — Risk + Implementation
 */

export type SectionId =
  | "BUDGET"
  | "AUTHORITY"
  | "NEED"
  | "TIMELINE"
  | "COMPLIANCE"
  | "FINANCIAL"
  | "COMPETITOR"
  | "ORGANIZATION"
  | "COVERAGE"
  | "RISK";

export interface Section {
  id: SectionId;
  title: string;
  caption: string;
  weight: number;
}

export const SECTIONS: Section[] = [
  { id: "BUDGET",       title: "Budget",                   caption: "Funded, defended, and aligned to fiscal cycle.",       weight: 15 },
  { id: "AUTHORITY",    title: "Authority",                caption: "Economic buyer named, engaged, sign-off path clear.",  weight: 15 },
  { id: "NEED",         title: "Need",                     caption: "Trigger event present, pain quantified.",              weight: 14 },
  { id: "TIMELINE",     title: "Timeline",                 caption: "Close date defensible, trigger event tied to date.",   weight: 12 },
  { id: "COMPLIANCE",   title: "Compliance + Legal",       caption: "Regulatory blockers cleared, legal path mapped.",      weight: 8  },
  { id: "FINANCIAL",    title: "Financial",                caption: "Buyer financially sound, not a tire-kicker.",          weight: 10 },
  { id: "COMPETITOR",   title: "Competitor",               caption: "Incumbent identified, our wedge is clear.",            weight: 8  },
  { id: "ORGANIZATION", title: "Organization + Readiness", caption: "Org can absorb the solution, change capacity exists.", weight: 10 },
  { id: "COVERAGE",     title: "People Network + Coverage",caption: "Champion coached, multi-threaded, blocker neutralized.",weight: 8  },
  { id: "RISK",         title: "Risk + Implementation",    caption: "Deployment risk owned, integration scoped, change-management cleared.", weight: 8 },
];

export interface Option {
  value: number;
  label: string;
  anchor: string;
  leftBorderColor: string;
}

export const GRADED_OPTIONS: Option[] = [
  { value: 4, label: "Strong yes — confirmed / fully in place",   anchor: "Strong",  leftBorderColor: "#15803D" },
  { value: 3, label: "Mostly yes — likely / mostly in place",     anchor: "Mostly",  leftBorderColor: "#22C55E" },
  { value: 2, label: "Partial — mixed signals / unclear",         anchor: "Partial", leftBorderColor: "#F59E0B" },
  { value: 1, label: "Weak — mostly missing / mostly no",         anchor: "Weak",    leftBorderColor: "#F97316" },
  { value: 0, label: "Absent — hard no / blocked",                anchor: "Absent",  leftBorderColor: "#DC2626" },
];

export interface CalcQuestion {
  id: string;
  section: SectionId;
  stem: string;
  helper?: string;
  isFollowUp?: boolean;
  parentId?: string;
  /** When true, included in Quick Score 7-question fast pass (main only). */
  quick?: boolean;
}

export interface FollowUpRule {
  parentId: string;
  triggerWhen: { points: number[] };
  ask: string[];
}

// ─── MAIN QUESTIONS (40 total) ────────────────────────────────────────────────

// ── 1. BUDGET (5 main) ─────────────────────────────────────────────────────
// B1 — funding status (quick)
// B2 — fiscal alignment
// B3 — budget release timing
// B4 — ROI threshold / payback ceiling
// B5 — approval threshold and capex vs opex routing

// ── 2. AUTHORITY (5 main) ──────────────────────────────────────────────────
// A1 — economic buyer named (quick)
// A2 — economic buyer direct engagement
// A3 — sign-off path mapped
// A4 — committee / multi-approver situation
// A5 — internal champion with authority to sponsor

// ── 3. NEED (4 main) ───────────────────────────────────────────────────────
// N1 — compelling event (quick)
// N2 — quantified pain in customer's own numbers
// N3 — initiative rank (is this the customer's top priority?)
// N4 — status quo cost confirmed by customer

// ── 4. TIMELINE (3 main) ───────────────────────────────────────────────────
// T1 — firm close date (quick)
// T2 — trigger event on customer side
// T3 — implementation window confirmed by customer ops team

// ── 5. COMPLIANCE + LEGAL (3 main) ─────────────────────────────────────────
// CL1 — regulatory / standards blockers identified
// CL2 — legal and procurement engaged early
// CL3 — IP / data / export controls scoped

// ── 6. FINANCIAL (2 main) ──────────────────────────────────────────────────
// F1 — buyer org financial health
// F2 — finance contact engaged and can fund within the timeline

// ── 7. COMPETITOR (4 main) ─────────────────────────────────────────────────
// CO1 — incumbent / status quo identified (quick)
// CO2 — active competing vendors named and leaning
// CO3 — customer's stated selection criteria
// CO4 — our proof story vs incumbent on the decisive dimension

// ── 8. ORGANIZATION + READINESS (4 main) ───────────────────────────────────
// OR1 — org capacity to absorb and deploy
// OR2 — change owner named with mandate
// OR3 — prior solution / failed initiative to account for
// OR4 — executive sponsorship for adoption post-implementation

// ── 9. PEOPLE NETWORK + COVERAGE (5 main) ──────────────────────────────────
// P1 — identified champion (quick)
// P2 — champion is actively selling internally
// P3 — multi-threaded (2+ independent contacts)
// P4 — blocker identified and being worked
// P5 — access to economic buyer direct (separate from champion)

// ── 10. RISK + IMPLEMENTATION (5 main) ─────────────────────────────────────
// R1 — technical owner named, integration scoped (quick)
// R2 — deployment risks identified and owned by customer
// R3 — data / migration risk scoped
// R4 — training / adoption capacity confirmed
// R5 — timeline risk: can deployment hit the customer's go-live date?

export const QUESTIONS: CalcQuestion[] = [

  // ── 1. BUDGET ──────────────────────────────────────────────────────────────
  {
    id: "B1",
    section: "BUDGET",
    quick: true,
    stem: "Is budget formally identified, defended by the customer, and aligned to the current fiscal year?",
    helper: "Hard no = no line item exists. Strong yes = written budget, fiscally aligned, name of person defending it.",
  },
  {
    id: "B2",
    section: "BUDGET",
    stem: "Is there a discrete budget line item for this purchase — not a hope that savings will fund it?",
    helper: "Deals built on projected savings routinely get reclassified in the next budget cycle.",
  },
  {
    id: "B3",
    section: "BUDGET",
    stem: "Is the budget released and available to spend before the target close date — or is it still pending approval at a higher level?",
    helper: "\"Budget exists\" and \"budget is approved to spend\" are two different gates.",
  },
  {
    id: "B4",
    section: "BUDGET",
    stem: "Has the customer stated a specific ROI threshold or payback ceiling — a number this deal must clear to get funded?",
    helper: "If they have a payback threshold, do you know whether your numbers clear it?",
  },
  {
    id: "B5",
    section: "BUDGET",
    stem: "Is the purchase routed through capex, opex, or a special project budget — and does that routing match the way the customer's finance team is expecting to fund it?",
    helper: "Capex vs opex misrouting is one of the most common late-stage deal killers in manufacturing.",
  },

  // ── 2. AUTHORITY ───────────────────────────────────────────────────────────
  {
    id: "A1",
    section: "AUTHORITY",
    quick: true,
    stem: "Is the economic buyer — the operator who can authorize this spend without further sign-off — named, confirmed, and aware of this deal?",
    helper: "Name the person. If you cannot name them, you do not have an economic buyer.",
  },
  {
    id: "A2",
    section: "AUTHORITY",
    stem: "Has the economic buyer been directly engaged in at least one conversation where they expressed personal interest in solving this problem?",
    helper: "Being briefed through a champion is not direct engagement.",
  },
  {
    id: "A3",
    section: "AUTHORITY",
    stem: "Is the full sign-off path — who approves, in what sequence, at what spending thresholds — mapped and confirmed by someone inside the account?",
    helper: "Surprises here are how deals stall at 90%. If you do not have this mapped, you are flying blind.",
  },
  {
    id: "A4",
    section: "AUTHORITY",
    stem: "Is there a committee, board vote, or multi-approver process that must run before a PO can be issued — and if so, is the schedule mapped?",
    helper: "In capital equipment and automation, committee approvals routinely add 30–90 days.",
  },
  {
    id: "A5",
    section: "AUTHORITY",
    stem: "Is there a named internal sponsor — above the day-to-day champion — who has the authority and willingness to back this initiative if it hits resistance?",
    helper: "Champions can lose political ground. A sponsor above them keeps the deal alive when the champion gets challenged.",
  },

  // ── 3. NEED ────────────────────────────────────────────────────────────────
  {
    id: "N1",
    section: "NEED",
    quick: true,
    stem: "Is there a firm or formal timeline — a line launch, production target, regulatory deadline, or contract expiry — that makes doing nothing more costly than acting?",
    helper: "Without a firm timeline, the customer can always wait one more quarter.",
  },
  {
    id: "N2",
    section: "NEED",
    stem: "Has the customer quantified the cost or operational impact of the current problem in their own numbers — not your model, their numbers?",
    helper: "If they have not put a dollar figure on the pain, the urgency is yours, not theirs.",
  },
  {
    id: "N3",
    section: "NEED",
    stem: "Is solving this problem in the customer's top three operational priorities for the year — or is it something they would like to get to eventually?",
    helper: "\"Nice to have\" initiatives get defunded when the next crisis hits the floor.",
  },
  {
    id: "N4",
    section: "NEED",
    stem: "Has the customer confirmed the cost of staying with the status quo — including the fully-loaded cost of workarounds, rework, and downtime — not just the obvious pain?",
    helper: "Status quo always has a cost. If they have not calculated it, the urgency gap is still open.",
  },

  // ── 4. TIMELINE ────────────────────────────────────────────────────────────
  {
    id: "T1",
    section: "TIMELINE",
    quick: true,
    stem: "Is there a firm, customer-driven close date you could defend to a forecasting committee — a date tied to something happening on their side?",
    helper: "Your quarter-end is not their close date. Attach it to their trigger event.",
  },
  {
    id: "T2",
    section: "TIMELINE",
    stem: "Is the close date tied to a specific trigger event on the customer's side — a line start, a contract renewal, a safety audit — that makes the date real?",
    helper: "A trigger event is what separates a forecast-ready close date from a hopeful one.",
  },
  {
    id: "T3",
    section: "TIMELINE",
    stem: "Has the customer's operations or project team confirmed they have the bandwidth to receive and implement this solution within the proposed window?",
    helper: "Facilities and production teams often have blackout periods that break close dates.",
  },

  // ── 5. COMPLIANCE + LEGAL ──────────────────────────────────────────────────
  {
    id: "CL1",
    section: "COMPLIANCE",
    stem: "Are any regulatory or standards-compliance blockers — ITAR, ISO, OSHA, EPA, safety certifications — identified and on a confirmed path to clearance?",
    helper: "Unidentified compliance requirements surface at the contract review stage and break timelines.",
  },
  {
    id: "CL2",
    section: "COMPLIANCE",
    stem: "Is legal and procurement on both sides engaged early enough to avoid a review cycle that will break the close date?",
    helper: "Legal reviews in manufacturing supply agreements routinely run 30–60 days. Early engagement is not optional.",
  },
  {
    id: "CL3",
    section: "COMPLIANCE",
    stem: "Are IP ownership, data handling, export control, and any proprietary process terms scoped and agreed in principle before the contract draft stage?",
    helper: "IP and export control disputes are the contracts that blow up at the finish line.",
  },

  // ── 6. FINANCIAL ───────────────────────────────────────────────────────────
  {
    id: "F1",
    section: "FINANCIAL",
    stem: "Is the buyer organization financially stable enough to see this purchase through — no distress, no active freeze, no pending ownership change?",
    helper: "M&A activity, credit downgrades, and quarterly earnings misses all trigger purchasing freezes.",
  },
  {
    id: "F2",
    section: "FINANCIAL",
    stem: "Has a finance contact on the customer side confirmed they can fund this within the projected timeline — not just that budget exists in principle?",
    helper: "Finance confirmation that the funds are accessible and allocated closes the gap between \"budget exists\" and \"deal can close.\"",
  },

  // ── 7. COMPETITOR ──────────────────────────────────────────────────────────
  {
    id: "CO1",
    section: "COMPETITOR",
    quick: true,
    stem: "Is the incumbent vendor or the status quo identified, and do you have a confirmed wedge — a specific reason the customer is willing to displace it?",
    helper: "Without a confirmed wedge, the incumbent wins by default.",
  },
  {
    id: "CO2",
    section: "COMPETITOR",
    stem: "Are all active competing vendors named — not just who you think is in the deal — and does your sponsor lean toward you on at least one decisive selection dimension?",
    helper: "\"I think it is just us and Company X\" is not competitive intelligence.",
  },
  {
    id: "CO3",
    section: "COMPETITOR",
    stem: "Does the customer have published or stated selection criteria — and do you know where you win and where you are exposed against each active competitor?",
    helper: "If you do not know the criteria, you cannot steer the evaluation.",
  },
  {
    id: "CO4",
    section: "COMPETITOR",
    stem: "Do you have a proof story — a reference customer in a comparable manufacturing environment — that directly addresses the incumbent's strongest advantage?",
    helper: "In manufacturing, a peer reference in the same vertical carries more weight than any technical spec.",
  },

  // ── 8. ORGANIZATION + READINESS ────────────────────────────────────────────
  {
    id: "OR1",
    section: "ORGANIZATION",
    stem: "Does the customer's operation have the people, IT infrastructure, and process bandwidth to absorb and deploy this solution within the projected timeline?",
    helper: "Organizational capacity is often the binding constraint — not budget or authority.",
  },
  {
    id: "OR2",
    section: "ORGANIZATION",
    stem: "Is there a named change owner on the customer side — with the mandate and authority to drive adoption past the implementation finish line?",
    helper: "Without a named change owner, implementations stall after go-live and customers blame the vendor.",
  },
  {
    id: "OR3",
    section: "ORGANIZATION",
    stem: "Is there a prior failed initiative or an existing solution the customer is replacing — and is the scar tissue from that experience mapped and addressed?",
    helper: "A previous failed implementation creates organizational resistance that does not show up on the org chart.",
  },
  {
    id: "OR4",
    section: "ORGANIZATION",
    stem: "Is there executive-level sponsorship for the adoption phase — not just for the purchase decision — so the solution does not get abandoned after go-live?",
    helper: "Post-go-live adoption failures are the fastest path to a failed renewal and a reference customer you cannot use.",
  },

  // ── 9. PEOPLE NETWORK + COVERAGE ───────────────────────────────────────────
  {
    id: "P1",
    section: "COVERAGE",
    quick: true,
    stem: "Is there an identified, coached champion inside the customer who is actively selling this deal internally on your behalf?",
    helper: "A champion who knows your value proposition, knows the internal politics, and is moving people is different from someone who likes you.",
  },
  {
    id: "P2",
    section: "COVERAGE",
    stem: "Is your champion actively selling this deal internally — scheduling meetings, aligning stakeholders, handling objections — or are they waiting for you to do all the work?",
    helper: "A passive champion is a friendly contact, not a champion.",
  },
  {
    id: "P3",
    section: "COVERAGE",
    stem: "Do you have active relationships with at least two independent threads inside the buying organization — so the deal survives if your primary contact leaves or goes quiet?",
    helper: "Single-threaded deals collapse. Multi-threading is non-negotiable.",
  },
  {
    id: "P4",
    section: "COVERAGE",
    stem: "Is any known blocker — a skeptical plant manager, a competing internal project, a budget owner who has not bought in — identified and actively being worked?",
    helper: "Unworked blockers do not go away. They resurface at signature time.",
  },
  {
    id: "P5",
    section: "COVERAGE",
    stem: "Do you have direct access to the economic buyer — not access through your champion, but a direct line where you can get a yes or a no?",
    helper: "If the economic buyer has never engaged directly with you, your champion is carrying the whole deal. That is a single point of failure.",
  },

  // ── 10. RISK + IMPLEMENTATION ──────────────────────────────────────────────
  {
    id: "R1",
    section: "RISK",
    quick: true,
    stem: "Has the customer's technical team confirmed they can absorb the integration without rebuilding their existing stack — and is there a named technical owner accountable for the cutover?",
    helper: "Integration complexity confirmed late is one of the fastest ways to blow a close date.",
  },
  {
    id: "R2",
    section: "RISK",
    stem: "Are the highest-impact deployment risks — change management, data migration, downtime windows, training capacity — identified, scoped, and owned by the customer's side?",
    helper: "If the customer has not owned these risks, they are still yours — even if the contract says otherwise.",
  },
  {
    id: "R3",
    section: "RISK",
    stem: "Is the data migration or system cutover scope confirmed — how much legacy data, who cleans it, what the cutover window is, and who is accountable if it slips?",
    helper: "Data migration surprises are the most common cause of implementation delays in manufacturing technology deals.",
  },
  {
    id: "R4",
    section: "RISK",
    stem: "Has the customer confirmed they have the training capacity — time on the floor, people available, shift coverage — to run adoption without production disruption?",
    helper: "Training capacity is almost always underestimated by the customer at purchase time.",
  },
  {
    id: "R5",
    section: "RISK",
    stem: "Can the deployment realistically hit the customer's go-live date given the implementation complexity, integration scope, and current resource commitments on both sides?",
    helper: "If your own team privately doubts the timeline, that is a risk worth surfacing now.",
  },

  // ─── FOLLOW-UP QUESTIONS ───────────────────────────────────────────────────
  // IDs: parentId + letter suffix (a, b, c...)

  // B1 follow-ups
  {
    id: "B1a", section: "BUDGET", isFollowUp: true, parentId: "B1",
    stem: "What is stopping the customer from formally committing budget to this project right now?",
    helper: "Is it a CFO gate, a competing priority, a timing issue, or something else?",
  },
  {
    id: "B1b", section: "BUDGET", isFollowUp: true, parentId: "B1",
    stem: "Who specifically on the customer's side is defending this budget line — do you have a name and a title?",
  },
  {
    id: "B1c", section: "BUDGET", isFollowUp: true, parentId: "B1",
    stem: "What is the risk that this budget gets reallocated before the projected close date?",
    helper: "Budget protected in Q1 planning is not always protected at Q3 reforecast.",
  },

  // B2 follow-ups
  {
    id: "B2a", section: "BUDGET", isFollowUp: true, parentId: "B2",
    stem: "Is the customer planning to fund this from projected cost savings or productivity gains — and has that model been validated by their finance team?",
    helper: "Savings-funded deals rarely survive a CFO review unless the model has been signed off.",
  },
  {
    id: "B2b", section: "BUDGET", isFollowUp: true, parentId: "B2",
    stem: "What is the specific budget line item — capital project, IT budget, maintenance budget, or other — and who controls it?",
  },

  // B3 follow-ups
  {
    id: "B3a", section: "BUDGET", isFollowUp: true, parentId: "B3",
    stem: "What is the approval step or event that needs to happen before the budget is released — and what is the timeline for that step?",
  },
  {
    id: "B3b", section: "BUDGET", isFollowUp: true, parentId: "B3",
    stem: "If the budget release is delayed, what happens to the close date — does the customer have flexibility or is this a hard dependency?",
  },

  // B4 follow-ups
  {
    id: "B4a", section: "BUDGET", isFollowUp: true, parentId: "B4",
    stem: "What is the specific ROI threshold or payback period the customer has stated — and do your numbers clear it?",
  },
  {
    id: "B4b", section: "BUDGET", isFollowUp: true, parentId: "B4",
    stem: "Has the customer ever walked away from a deal that did not clear their ROI threshold — or is the threshold negotiable in practice?",
  },

  // B5 follow-ups
  {
    id: "B5a", section: "BUDGET", isFollowUp: true, parentId: "B5",
    stem: "Has the customer's finance team confirmed the capex vs opex routing and what that means for the approval path and timeline?",
  },
  {
    id: "B5b", section: "BUDGET", isFollowUp: true, parentId: "B5",
    stem: "Is there a capex freeze, depreciation policy, or lease vs buy constraint that affects how this deal gets structured?",
  },

  // A1 follow-ups
  {
    id: "A1a", section: "AUTHORITY", isFollowUp: true, parentId: "A1",
    stem: "Who is the economic buyer — name and title — and how do you know they can authorize this spend without a higher approval?",
    helper: "\"The plant manager\" is not an answer unless you know their spending authority.",
  },
  {
    id: "A1b", section: "AUTHORITY", isFollowUp: true, parentId: "A1",
    stem: "What is blocking you from identifying or accessing the economic buyer directly?",
  },
  {
    id: "A1c", section: "AUTHORITY", isFollowUp: true, parentId: "A1",
    stem: "Is the person you have been working with a champion, an influencer, or the actual decision-maker — and how do you know the difference?",
  },

  // A2 follow-ups
  {
    id: "A2a", section: "AUTHORITY", isFollowUp: true, parentId: "A2",
    stem: "What specifically did the economic buyer say in that direct conversation — and did they commit to any next action on their side?",
    helper: "Interest without commitment is curiosity, not engagement.",
  },
  {
    id: "A2b", section: "AUTHORITY", isFollowUp: true, parentId: "A2",
    stem: "What is the path to a direct conversation with the economic buyer — who can make that introduction, and what is the timeline?",
  },

  // A3 follow-ups
  {
    id: "A3a", section: "AUTHORITY", isFollowUp: true, parentId: "A3",
    stem: "Walk through the approval sequence — who is step one, what threshold triggers a second approver, and where does the final signature land?",
  },
  {
    id: "A3b", section: "AUTHORITY", isFollowUp: true, parentId: "A3",
    stem: "Is there a step in the approval chain where the deal is most likely to stall — and what is the plan to address it before it gets there?",
  },
  {
    id: "A3c", section: "AUTHORITY", isFollowUp: true, parentId: "A3",
    stem: "Has the sign-off path been confirmed by someone inside the account — or is it your assumption based on org chart research?",
  },

  // A4 follow-ups
  {
    id: "A4a", section: "AUTHORITY", isFollowUp: true, parentId: "A4",
    stem: "What is the committee or board review schedule — when does it meet, what is the submission deadline, and what format do they need?",
  },
  {
    id: "A4b", section: "AUTHORITY", isFollowUp: true, parentId: "A4",
    stem: "Do you have access to at least one committee or board member directly — not just the person presenting on your behalf?",
  },

  // A5 follow-ups
  {
    id: "A5a", section: "AUTHORITY", isFollowUp: true, parentId: "A5",
    stem: "Has the internal sponsor explicitly committed to backing this initiative if it hits budget or political resistance — or is that your assumption?",
  },
  {
    id: "A5b", section: "AUTHORITY", isFollowUp: true, parentId: "A5",
    stem: "What is the senior sponsor's personal stake in this initiative — is their own performance measured against the outcome this solution delivers?",
    helper: "A sponsor whose own metrics depend on this outcome fights much harder than one who is just being supportive.",
  },

  // N1 follow-ups
  {
    id: "N1a", section: "NEED", isFollowUp: true, parentId: "N1",
    stem: "What is the specific firm or formal timeline — name it, date it, and explain what happens if the customer misses it?",
  },
  {
    id: "N1b", section: "NEED", isFollowUp: true, parentId: "N1",
    stem: "Has the customer explicitly connected the firm timeline to the need for this solution — or is that connection your interpretation?",
  },
  {
    id: "N1c", section: "NEED", isFollowUp: true, parentId: "N1",
    stem: "What is stopping the customer from treating this as urgent even without a hard external deadline?",
  },

  // N2 follow-ups
  {
    id: "N2a", section: "NEED", isFollowUp: true, parentId: "N2",
    stem: "What specific number did the customer put on the problem — downtime cost, scrap rate, labor hours wasted — and who validated it?",
  },
  {
    id: "N2b", section: "NEED", isFollowUp: true, parentId: "N2",
    stem: "Without a quantified pain number, what does the customer believe the problem is worth solving — and do they care enough to act on that belief?",
  },

  // N3 follow-ups
  {
    id: "N3a", section: "NEED", isFollowUp: true, parentId: "N3",
    stem: "What are the customer's top one or two operational priorities right now — and where does this initiative rank on that list?",
  },
  {
    id: "N3b", section: "NEED", isFollowUp: true, parentId: "N3",
    stem: "What would have to happen for this initiative to move up the priority list — is there a trigger that makes it non-optional?",
  },

  // N4 follow-ups
  {
    id: "N4a", section: "NEED", isFollowUp: true, parentId: "N4",
    stem: "Has the customer calculated and accepted the fully-loaded cost of staying with the status quo — workarounds, manual labor, quality failures, opportunity cost?",
  },
  {
    id: "N4b", section: "NEED", isFollowUp: true, parentId: "N4",
    stem: "Is the customer comfortable with the status quo because they have adapted to it — meaning the pain is no longer visible to them even though it is real?",
  },

  // T1 follow-ups
  {
    id: "T1a", section: "TIMELINE", isFollowUp: true, parentId: "T1",
    stem: "What is the customer-stated close date — and what event on their side drives it?",
  },
  {
    id: "T1b", section: "TIMELINE", isFollowUp: true, parentId: "T1",
    stem: "What happens to the deal if the close date slips by 30 days — does the trigger event become less urgent, or does it disappear?",
  },
  {
    id: "T1c", section: "TIMELINE", isFollowUp: true, parentId: "T1",
    stem: "What specifically makes the close date you are forecasting real — whose calendar does it sit on other than yours?",
  },

  // T2 follow-ups
  {
    id: "T2a", section: "TIMELINE", isFollowUp: true, parentId: "T2",
    stem: "What is the trigger event — name it, date it, and explain the consequence if the customer misses it without this solution in place?",
  },
  {
    id: "T2b", section: "TIMELINE", isFollowUp: true, parentId: "T2",
    stem: "If the only date on the table is your quarter-end, what would it take to create a customer-side urgency event before then?",
  },

  // T3 follow-ups
  {
    id: "T3a", section: "TIMELINE", isFollowUp: true, parentId: "T3",
    stem: "What did the operations or project team say about bandwidth — do they have the floor time and headcount to onboard this in the projected window?",
  },
  {
    id: "T3b", section: "TIMELINE", isFollowUp: true, parentId: "T3",
    stem: "Are there production blackout periods, seasonal shutdowns, or competing project commitments that make the implementation window unrealistic?",
  },

  // CL1 follow-ups
  {
    id: "CL1a", section: "COMPLIANCE", isFollowUp: true, parentId: "CL1",
    stem: "Which specific regulatory or standards requirements apply — ITAR, ISO 9001, ISO 14001, OSHA PSM, EPA, safety certs — and who on the customer's side owns the clearance path?",
  },
  {
    id: "CL1b", section: "COMPLIANCE", isFollowUp: true, parentId: "CL1",
    stem: "What is the timeline for compliance clearance — and does it fit inside the projected close-to-go-live window?",
  },

  // CL2 follow-ups
  {
    id: "CL2a", section: "COMPLIANCE", isFollowUp: true, parentId: "CL2",
    stem: "At what point in the cycle are legal and procurement being engaged — and is that early enough to prevent a review delay that breaks the close date?",
  },
  {
    id: "CL2b", section: "COMPLIANCE", isFollowUp: true, parentId: "CL2",
    stem: "Has procurement on the customer's side seen the commercial terms — and is there anything in the standard MSA that typically creates a delay or exception process?",
  },

  // CL3 follow-ups
  {
    id: "CL3a", section: "COMPLIANCE", isFollowUp: true, parentId: "CL3",
    stem: "Are there IP ownership or proprietary process terms in this deal that require a custom agreement — and has that been flagged to both legal teams?",
  },
  {
    id: "CL3b", section: "COMPLIANCE", isFollowUp: true, parentId: "CL3",
    stem: "Does the customer operate under export control restrictions — ITAR, EAR, or OFAC — that affect how you can deliver or support the solution?",
  },

  // F1 follow-ups
  {
    id: "F1a", section: "FINANCIAL", isFollowUp: true, parentId: "F1",
    stem: "Are there any signals of financial distress — missed payments to other vendors, news of layoffs, credit rating changes, ownership changes — that you have looked into and cleared?",
  },
  {
    id: "F1b", section: "FINANCIAL", isFollowUp: true, parentId: "F1",
    stem: "What is the customer's current financial posture — are they investing or in cost-reduction mode — and how does this deal fit that posture?",
  },
  {
    id: "F1c", section: "FINANCIAL", isFollowUp: true, parentId: "F1",
    stem: "Is there an acquisition, merger, or divestiture in process that could freeze purchasing decisions before this deal closes?",
  },

  // F2 follow-ups
  {
    id: "F2a", section: "FINANCIAL", isFollowUp: true, parentId: "F2",
    stem: "What did the finance contact specifically confirm — that the funds are allocated and accessible, or just that the budget category exists?",
  },
  {
    id: "F2b", section: "FINANCIAL", isFollowUp: true, parentId: "F2",
    stem: "What is blocking a finance contact from being engaged — is that a process issue, a relationship gap, or a signal that the deal is not prioritized internally?",
  },

  // CO1 follow-ups
  {
    id: "CO1a", section: "COMPETITOR", isFollowUp: true, parentId: "CO1",
    stem: "What specifically is the wedge — the one dimension where the customer has said the incumbent fails and we win?",
    helper: "A general preference is not a wedge. A specific, quantified failure of the incumbent is a wedge.",
  },
  {
    id: "CO1b", section: "COMPETITOR", isFollowUp: true, parentId: "CO1",
    stem: "Has the customer articulated why they are willing to absorb the switching cost and disruption of displacing the incumbent?",
  },
  {
    id: "CO1c", section: "COMPETITOR", isFollowUp: true, parentId: "CO1",
    stem: "What is the status quo or incumbent — name it — and what does the customer say when you ask why they want to move away from it?",
  },

  // CO2 follow-ups
  {
    id: "CO2a", section: "COMPETITOR", isFollowUp: true, parentId: "CO2",
    stem: "Who are the other vendors in this evaluation — named — and what does your sponsor say about how you rank against each one?",
  },
  {
    id: "CO2b", section: "COMPETITOR", isFollowUp: true, parentId: "CO2",
    stem: "Which competitor is the customer's fallback if you do not close — and what would that cost the customer in terms of outcome quality?",
  },

  // CO3 follow-ups
  {
    id: "CO3a", section: "COMPETITOR", isFollowUp: true, parentId: "CO3",
    stem: "What are the customer's stated selection criteria — in priority order — and where are you objectively strong and where are you exposed?",
  },
  {
    id: "CO3b", section: "COMPETITOR", isFollowUp: true, parentId: "CO3",
    stem: "Are you influencing the selection criteria, or are you responding to a set of criteria that was written before you were in the deal?",
  },

  // CO4 follow-ups
  {
    id: "CO4a", section: "COMPETITOR", isFollowUp: true, parentId: "CO4",
    stem: "What is the specific reference customer and story — comparable vertical, comparable problem, comparable scale — that you can put in front of the economic buyer?",
  },
  {
    id: "CO4b", section: "COMPETITOR", isFollowUp: true, parentId: "CO4",
    stem: "Has the customer asked for references or case studies — and if so, have you provided ones that directly address the incumbent's strongest claim?",
  },

  // OR1 follow-ups
  {
    id: "OR1a", section: "ORGANIZATION", isFollowUp: true, parentId: "OR1",
    stem: "What specific capacity gaps — headcount, IT infrastructure, bandwidth — have been identified, and is there a plan to close them before implementation starts?",
  },
  {
    id: "OR1b", section: "ORGANIZATION", isFollowUp: true, parentId: "OR1",
    stem: "How many other major initiatives is the customer's ops team running in parallel — and is this one funded and staffed relative to those?",
  },

  // OR2 follow-ups
  {
    id: "OR2a", section: "ORGANIZATION", isFollowUp: true, parentId: "OR2",
    stem: "Who is the named change owner — title, name, and do they have the authority to enforce adoption across departments that resist?",
  },
  {
    id: "OR2b", section: "ORGANIZATION", isFollowUp: true, parentId: "OR2",
    stem: "What happens post-go-live without a named change owner — who is accountable for driving adoption and resolving resistance on the floor?",
  },

  // OR3 follow-ups
  {
    id: "OR3a", section: "ORGANIZATION", isFollowUp: true, parentId: "OR3",
    stem: "What happened with the previous initiative — why did it fail or stall — and how does this deal address that failure mode directly?",
  },
  {
    id: "OR3b", section: "ORGANIZATION", isFollowUp: true, parentId: "OR3",
    stem: "Who in the organization remembers the previous failure and is likely to resist this initiative because of it?",
  },

  // OR4 follow-ups
  {
    id: "OR4a", section: "ORGANIZATION", isFollowUp: true, parentId: "OR4",
    stem: "Has the executive sponsor explicitly committed to driving adoption — not just approving the purchase — after go-live?",
  },
  {
    id: "OR4b", section: "ORGANIZATION", isFollowUp: true, parentId: "OR4",
    stem: "Is the executive sponsor's own performance tied to the post-implementation outcome — or does their involvement end at signature?",
  },

  // P1 follow-ups
  {
    id: "P1a", section: "COVERAGE", isFollowUp: true, parentId: "P1",
    stem: "Who is the champion — name and title — and what specifically have they done to sell this deal internally in the last 30 days?",
    helper: "Recent activity is the test. A champion who has not moved anything in 30 days is not active.",
  },
  {
    id: "P1b", section: "COVERAGE", isFollowUp: true, parentId: "P1",
    stem: "What is blocking you from developing a champion inside this account — is it access, relationships, or something about the deal itself?",
  },
  {
    id: "P1c", section: "COVERAGE", isFollowUp: true, parentId: "P1",
    stem: "Has your champion been tested — faced a hard question from a skeptic internally — and held up? Or have they only operated in friendly territory?",
    helper: "An untested champion is a risk you will discover at the worst moment.",
  },

  // P2 follow-ups
  {
    id: "P2a", section: "COVERAGE", isFollowUp: true, parentId: "P2",
    stem: "What specific internal actions has the champion taken — stakeholder meetings scheduled, objections handled, executive briefings arranged — in the last 30 days?",
  },
  {
    id: "P2b", section: "COVERAGE", isFollowUp: true, parentId: "P2",
    stem: "What would motivate the champion to become more active internally — is there a personal win for them if this deal closes?",
    helper: "Champions move when their own agenda is served. What does this deal do for them personally?",
  },

  // P3 follow-ups
  {
    id: "P3a", section: "COVERAGE", isFollowUp: true, parentId: "P3",
    stem: "Name the second independent contact — title, department, and your last meaningful interaction with them.",
  },
  {
    id: "P3b", section: "COVERAGE", isFollowUp: true, parentId: "P3",
    stem: "What is the plan to add a second thread — which department or function should you be in, and who makes the introduction?",
  },

  // P4 follow-ups
  {
    id: "P4a", section: "COVERAGE", isFollowUp: true, parentId: "P4",
    stem: "Name the blocker — title and objection — and describe the specific approach being used to neutralize or route around them.",
  },
  {
    id: "P4b", section: "COVERAGE", isFollowUp: true, parentId: "P4",
    stem: "What is the risk that this blocker surfaces at the contract stage rather than being resolved in the evaluation phase?",
  },

  // P5 follow-ups
  {
    id: "P5a", section: "COVERAGE", isFollowUp: true, parentId: "P5",
    stem: "What would it take to get a direct conversation with the economic buyer — who can make that introduction and what is the timeline?",
  },
  {
    id: "P5b", section: "COVERAGE", isFollowUp: true, parentId: "P5",
    stem: "Is your champion willing and able to arrange direct economic buyer access — and if not, what does that tell you about their internal standing?",
  },

  // R1 follow-ups
  {
    id: "R1a", section: "RISK", isFollowUp: true, parentId: "R1",
    stem: "Who is the named technical owner — name and title — and what specifically have they agreed to own for the cutover?",
  },
  {
    id: "R1b", section: "RISK", isFollowUp: true, parentId: "R1",
    stem: "What integration touchpoints or existing systems are involved — and has the technical team walked through each one with your implementation team?",
  },
  {
    id: "R1c", section: "RISK", isFollowUp: true, parentId: "R1",
    stem: "What is blocking the customer's technical team from committing to the integration — is it a resource issue, a system constraint, or a priority conflict?",
  },

  // R2 follow-ups
  {
    id: "R2a", section: "RISK", isFollowUp: true, parentId: "R2",
    stem: "Which deployment risks have been formally accepted and owned by the customer — in writing or in a documented mutual plan?",
  },
  {
    id: "R2b", section: "RISK", isFollowUp: true, parentId: "R2",
    stem: "Which of the deployment risks — change management, data migration, downtime, training — is the highest probability of breaking the go-live date?",
  },

  // R3 follow-ups
  {
    id: "R3a", section: "RISK", isFollowUp: true, parentId: "R3",
    stem: "What is the data migration scope — volume, format, cleanliness of legacy data — and who is responsible for data preparation on the customer's side?",
  },
  {
    id: "R3b", section: "RISK", isFollowUp: true, parentId: "R3",
    stem: "Has a data quality assessment been done — and are there known data issues that will require customer effort before migration can proceed?",
  },

  // R4 follow-ups
  {
    id: "R4a", section: "RISK", isFollowUp: true, parentId: "R4",
    stem: "How much floor time has the customer committed to training — scheduled, not aspirational — and does it align with what your implementation team requires?",
  },
  {
    id: "R4b", section: "RISK", isFollowUp: true, parentId: "R4",
    stem: "Is there a shift pattern, production schedule, or safety constraint that limits when training can happen — and has that been factored into the implementation plan?",
  },

  // R5 follow-ups
  {
    id: "R5a", section: "RISK", isFollowUp: true, parentId: "R5",
    stem: "What is your honest internal estimate of the earliest realistic go-live date given the current implementation complexity and resource commitments?",
  },
  {
    id: "R5b", section: "RISK", isFollowUp: true, parentId: "R5",
    stem: "If the go-live date slips, what is the customer-side consequence — and does that consequence motivate them to resource the project properly?",
  },
];

// ─── QUICK SCORE (7 questions, no follow-ups) ─────────────────────────────────
export const QUICK_QUESTIONS: CalcQuestion[] = QUESTIONS.filter((q) => q.quick);

// ─── MAIN QUESTIONS ONLY (for confidence calculation) ────────────────────────
export const MAIN_QUESTIONS: CalcQuestion[] = QUESTIONS.filter((q) => !q.isFollowUp);

// ─── FOLLOW-UP RULES ──────────────────────────────────────────────────────────
// triggerWhen.points: array of point values that fire this rule
//   [0, 1]    = Absent or Weak (hard no / mostly no) → drill into blocker
//   [2]       = Partial (mixed signals) → drill into ambiguity
//   [3, 4]    = Mostly yes or Strong yes → verify (use sparingly)
//   [0, 1, 2] = Any weak/partial answer → multi-level drill

export const FOLLOW_UP_RULES: FollowUpRule[] = [
  // ── BUDGET ──
  { parentId: "B1", triggerWhen: { points: [0, 1] },    ask: ["B1a", "B1b"] },
  { parentId: "B1", triggerWhen: { points: [2] },        ask: ["B1b", "B1c"] },
  { parentId: "B1", triggerWhen: { points: [3, 4] },     ask: ["B1b"] },          // verify: who is defending it?
  { parentId: "B2", triggerWhen: { points: [0, 1] },    ask: ["B2a", "B2b"] },
  { parentId: "B2", triggerWhen: { points: [2] },        ask: ["B2b"] },
  { parentId: "B3", triggerWhen: { points: [0, 1, 2] }, ask: ["B3a", "B3b"] },
  { parentId: "B4", triggerWhen: { points: [3, 4] },     ask: ["B4a"] },          // verify: do your numbers clear it?
  { parentId: "B4", triggerWhen: { points: [0, 1, 2] }, ask: ["B4a", "B4b"] },
  { parentId: "B5", triggerWhen: { points: [0, 1, 2] }, ask: ["B5a", "B5b"] },

  // ── AUTHORITY ──
  { parentId: "A1", triggerWhen: { points: [0, 1] },    ask: ["A1a", "A1b", "A1c"] },
  { parentId: "A1", triggerWhen: { points: [2] },        ask: ["A1a", "A1c"] },
  { parentId: "A1", triggerWhen: { points: [3, 4] },     ask: ["A1a"] },          // verify: name the person
  { parentId: "A2", triggerWhen: { points: [0, 1] },    ask: ["A2a", "A2b"] },
  { parentId: "A2", triggerWhen: { points: [2] },        ask: ["A2a"] },
  { parentId: "A3", triggerWhen: { points: [0, 1] },    ask: ["A3a", "A3b", "A3c"] },
  { parentId: "A3", triggerWhen: { points: [2] },        ask: ["A3a", "A3c"] },
  { parentId: "A3", triggerWhen: { points: [3, 4] },     ask: ["A3c"] },          // verify: confirmed internally?
  { parentId: "A4", triggerWhen: { points: [2, 3, 4] }, ask: ["A4a", "A4b"] },   // any committee is a risk worth scoping
  { parentId: "A5", triggerWhen: { points: [0, 1] },    ask: ["A5a", "A5b"] },
  { parentId: "A5", triggerWhen: { points: [3, 4] },     ask: ["A5b"] },          // verify: do they have personal stake?

  // ── NEED ──
  { parentId: "N1", triggerWhen: { points: [0, 1] },    ask: ["N1a", "N1b", "N1c"] },
  { parentId: "N1", triggerWhen: { points: [2] },        ask: ["N1a", "N1b"] },
  { parentId: "N2", triggerWhen: { points: [0, 1] },    ask: ["N2a", "N2b"] },
  { parentId: "N2", triggerWhen: { points: [2] },        ask: ["N2a"] },
  { parentId: "N3", triggerWhen: { points: [0, 1, 2] }, ask: ["N3a", "N3b"] },
  { parentId: "N4", triggerWhen: { points: [0, 1] },    ask: ["N4a", "N4b"] },
  { parentId: "N4", triggerWhen: { points: [2] },        ask: ["N4a"] },

  // ── TIMELINE ──
  { parentId: "T1", triggerWhen: { points: [0, 1] },    ask: ["T1a", "T1b", "T1c"] },
  { parentId: "T1", triggerWhen: { points: [2] },        ask: ["T1a", "T1c"] },
  { parentId: "T1", triggerWhen: { points: [3, 4] },     ask: ["T1c"] },          // verify: whose calendar?
  { parentId: "T2", triggerWhen: { points: [0, 1] },    ask: ["T2a", "T2b"] },
  { parentId: "T2", triggerWhen: { points: [2] },        ask: ["T2a"] },
  { parentId: "T3", triggerWhen: { points: [0, 1, 2] }, ask: ["T3a", "T3b"] },

  // ── COMPLIANCE + LEGAL ──
  { parentId: "CL1", triggerWhen: { points: [0, 1, 2] }, ask: ["CL1a", "CL1b"] },
  { parentId: "CL2", triggerWhen: { points: [0, 1, 2] }, ask: ["CL2a", "CL2b"] },
  { parentId: "CL3", triggerWhen: { points: [0, 1, 2] }, ask: ["CL3a", "CL3b"] },

  // ── FINANCIAL ──
  { parentId: "F1", triggerWhen: { points: [0, 1, 2] }, ask: ["F1a", "F1b", "F1c"] },
  { parentId: "F1", triggerWhen: { points: [3, 4] },     ask: ["F1c"] },          // verify: any pending M&A?
  { parentId: "F2", triggerWhen: { points: [0, 1] },    ask: ["F2a", "F2b"] },
  { parentId: "F2", triggerWhen: { points: [2] },        ask: ["F2a"] },

  // ── COMPETITOR ──
  { parentId: "CO1", triggerWhen: { points: [0, 1] },    ask: ["CO1a", "CO1b", "CO1c"] },
  { parentId: "CO1", triggerWhen: { points: [2] },        ask: ["CO1a", "CO1c"] },
  { parentId: "CO1", triggerWhen: { points: [3, 4] },     ask: ["CO1a"] },        // verify: describe the wedge
  { parentId: "CO2", triggerWhen: { points: [0, 1, 2] }, ask: ["CO2a", "CO2b"] },
  { parentId: "CO3", triggerWhen: { points: [0, 1, 2] }, ask: ["CO3a", "CO3b"] },
  { parentId: "CO4", triggerWhen: { points: [0, 1, 2] }, ask: ["CO4a", "CO4b"] },
  { parentId: "CO4", triggerWhen: { points: [3, 4] },     ask: ["CO4a"] },        // verify: which reference customer?

  // ── ORGANIZATION + READINESS ──
  { parentId: "OR1", triggerWhen: { points: [0, 1, 2] }, ask: ["OR1a", "OR1b"] },
  { parentId: "OR2", triggerWhen: { points: [0, 1] },    ask: ["OR2a", "OR2b"] },
  { parentId: "OR2", triggerWhen: { points: [2] },        ask: ["OR2a"] },
  { parentId: "OR3", triggerWhen: { points: [2, 3, 4] }, ask: ["OR3a", "OR3b"] }, // any prior initiative → drill
  { parentId: "OR4", triggerWhen: { points: [0, 1, 2] }, ask: ["OR4a", "OR4b"] },
  { parentId: "OR4", triggerWhen: { points: [3, 4] },     ask: ["OR4b"] },        // verify: personal stake?

  // ── PEOPLE NETWORK + COVERAGE ──
  { parentId: "P1", triggerWhen: { points: [0, 1] },    ask: ["P1a", "P1b"] },
  { parentId: "P1", triggerWhen: { points: [2] },        ask: ["P1a", "P1c"] },
  { parentId: "P1", triggerWhen: { points: [3, 4] },     ask: ["P1c"] },          // verify: tested under fire?
  { parentId: "P2", triggerWhen: { points: [0, 1, 2] }, ask: ["P2a", "P2b"] },
  { parentId: "P3", triggerWhen: { points: [0, 1] },    ask: ["P3a", "P3b"] },
  { parentId: "P3", triggerWhen: { points: [2] },        ask: ["P3b"] },
  { parentId: "P4", triggerWhen: { points: [2, 3, 4] }, ask: ["P4a", "P4b"] },   // any blocker → scope it
  { parentId: "P5", triggerWhen: { points: [0, 1, 2] }, ask: ["P5a", "P5b"] },
  { parentId: "P5", triggerWhen: { points: [3, 4] },     ask: ["P5b"] },          // verify: via champion = risk

  // ── RISK + IMPLEMENTATION ──
  { parentId: "R1", triggerWhen: { points: [0, 1] },    ask: ["R1a", "R1b", "R1c"] },
  { parentId: "R1", triggerWhen: { points: [2] },        ask: ["R1a", "R1b"] },
  { parentId: "R1", triggerWhen: { points: [3, 4] },     ask: ["R1a"] },          // verify: name the technical owner
  { parentId: "R2", triggerWhen: { points: [0, 1, 2] }, ask: ["R2a", "R2b"] },
  { parentId: "R3", triggerWhen: { points: [0, 1, 2] }, ask: ["R3a", "R3b"] },
  { parentId: "R4", triggerWhen: { points: [0, 1, 2] }, ask: ["R4a", "R4b"] },
  { parentId: "R5", triggerWhen: { points: [0, 1, 2] }, ask: ["R5a", "R5b"] },
  { parentId: "R5", triggerWhen: { points: [3, 4] },     ask: ["R5a"] },          // verify: honest estimate
];

// ─── SCORING ──────────────────────────────────────────────────────────────────

export type Answers = Record<string, number | null>;

export interface SectionScore {
  id: SectionId;
  title: string;
  pct: number | null;
  coveragePct: number;
  answered: number;
  total: number;
  followUpsAnswered: number;
}

export interface CalculatorResult {
  composite: number;
  band: "GREEN" | "AMBER" | "RED";
  verdict: "go-forward" | "work-the-gaps" | "cut-bait";
  verdictLine: string;
  confidenceTag: "high" | "medium" | "low";
  answeredCount: number;
  totalCount: number;
  mainAnsweredCount: number;
  mainTotalCount: number;
  followUpAnsweredCount: number;
  sections: SectionScore[];
  strongest: SectionId[];
  weakest: SectionId[];
}

/**
 * computeScore — v2.3 composite math.
 *
 * composite_0_100 = round( (sum_points_answered / (4 × count_answered)) × 100 )
 * Only answered (non-null) questions count.
 * Confidence: based on MAIN question completion rate only.
 * high ≥80% main answered, medium 50–79%, low <50%.
 * RAG: 70+ green, 50–69 amber, <50 red.
 *
 * answeredQuestions: the set of questions actually answered (main + follow-ups fired)
 * mainQuestions: used for confidence calculation
 */
export function computeScore(
  answers: Answers,
  answeredQuestions: CalcQuestion[] = MAIN_QUESTIONS,
  mainQuestionsForConfidence: CalcQuestion[] = MAIN_QUESTIONS,
): CalculatorResult {
  const totalCount = answeredQuestions.length;

  const answeredEntries = answeredQuestions.filter(
    (q) => answers[q.id] !== undefined && answers[q.id] !== null,
  );
  const answeredCount = answeredEntries.length;

  const sumPoints = answeredEntries.reduce(
    (acc, q) => acc + (answers[q.id] as number),
    0,
  );
  const composite =
    answeredCount > 0
      ? Math.round((sumPoints / (4 * answeredCount)) * 100)
      : 0;

  // Confidence based on main questions only
  const mainAnsweredCount = mainQuestionsForConfidence.filter(
    (q) => answers[q.id] !== undefined && answers[q.id] !== null,
  ).length;
  const mainTotalCount = mainQuestionsForConfidence.length;
  const mainAnsweredRatio = mainTotalCount > 0 ? mainAnsweredCount / mainTotalCount : 0;
  const confidenceTag: CalculatorResult["confidenceTag"] =
    mainAnsweredRatio >= 0.8 ? "high" : mainAnsweredRatio >= 0.5 ? "medium" : "low";

  // Follow-up count
  const followUpAnsweredCount = answeredEntries.filter(
    (q) => q.isFollowUp,
  ).length;

  // Band
  const band: CalculatorResult["band"] =
    composite >= 70 ? "GREEN" : composite >= 50 ? "AMBER" : "RED";

  // Verdict
  let verdict: CalculatorResult["verdict"];
  let verdictLine: string;
  if (composite >= 70) {
    verdict = "go-forward";
    verdictLine = "Worth pursuing — strong gates clear.";
  } else if (composite >= 50) {
    verdict = "work-the-gaps";
    verdictLine = "Mixed signals — work the gaps before you go further.";
  } else {
    verdict = "cut-bait";
    verdictLine = "Cut bait — gates not viable, save the time.";
  }

  // Per-section breakdown
  const sectionsInUse = SECTIONS.filter((s) =>
    answeredQuestions.some((q) => q.section === s.id),
  );

  const sectionScores: SectionScore[] = sectionsInUse.map((s) => {
    const sectionQs = answeredQuestions.filter((q) => q.section === s.id);
    const sectionMainQs = sectionQs.filter((q) => !q.isFollowUp);
    const sectionFollowUpQs = sectionQs.filter((q) => q.isFollowUp);
    const answeredQs = sectionQs.filter(
      (q) => answers[q.id] !== undefined && answers[q.id] !== null,
    );
    const answeredFollowUps = sectionFollowUpQs.filter(
      (q) => answers[q.id] !== undefined && answers[q.id] !== null,
    );
    const sectionSum = answeredQs.reduce(
      (acc, q) => acc + (answers[q.id] as number),
      0,
    );
    const pct =
      answeredQs.length > 0
        ? Math.round((sectionSum / (4 * answeredQs.length)) * 100)
        : null;
    const coveragePct = sectionMainQs.length > 0
      ? Math.round(
          (answeredQs.filter((q) => !q.isFollowUp).length / sectionMainQs.length) * 100,
        )
      : 0;
    return {
      id: s.id,
      title: s.title,
      pct,
      coveragePct,
      answered: answeredQs.length,
      total: sectionQs.length,
      followUpsAnswered: answeredFollowUps.length,
    };
  });

  // Strongest / weakest by pct
  const ranked = [...sectionScores]
    .filter((s) => s.pct !== null)
    .sort((a, b) => (b.pct as number) - (a.pct as number));
  const strongest = ranked.slice(0, 2).map((s) => s.id);
  const weakest = ranked.slice(-2).map((s) => s.id);

  return {
    composite,
    band,
    verdict,
    verdictLine,
    confidenceTag,
    answeredCount,
    totalCount,
    mainAnsweredCount,
    mainTotalCount,
    followUpAnsweredCount,
    sections: sectionScores,
    strongest,
    weakest,
  };
}

// ─── COMMENTARY ───────────────────────────────────────────────────────────────

export const SECTION_STRENGTH_LINES: Record<SectionId, string> = {
  BUDGET:       "Budget is named, defended, and fiscal-aligned.",
  AUTHORITY:    "The economic buyer is mapped and engaged.",
  NEED:         "A firm timeline is in place and pain is quantified.",
  TIMELINE:     "Close date is defensible and tied to a real trigger.",
  COMPLIANCE:   "Compliance and legal paths are clear and moving.",
  FINANCIAL:    "The buyer is financially stable and not a tire-kicker.",
  COMPETITOR:   "Incumbent is identified and you have a confirmed wedge.",
  ORGANIZATION: "The customer can absorb and deploy the solution.",
  COVERAGE:     "Champion is active, deal is multi-threaded, blockers worked.",
  RISK:         "Deployment risks are owned, integration is scoped, and the customer's technical team is accountable.",
};

export const SECTION_RISK_LINES: Record<SectionId, string> = {
  BUDGET:       "Budget is unconfirmed — get a written, fiscally aligned figure before commit.",
  AUTHORITY:    "Economic buyer is fuzzy — name the operator who can sign and engage them.",
  NEED:         "No firm timeline or unquantified pain — the customer can wait, which means they will.",
  TIMELINE:     "Close date is not tied to a customer trigger — it will slip.",
  COMPLIANCE:   "Compliance or legal path is unclear — engage both sides early or it breaks the date.",
  FINANCIAL:    "Buyer financial health is uncertain — qualify before investing more time.",
  COMPETITOR:   "Incumbent or competitor picture is murky — name what you are replacing and why.",
  ORGANIZATION: "Customer cannot absorb the solution yet — implementation risk is a deal risk.",
  COVERAGE:     "Coverage is single-threaded or champion is uncoached — multi-thread before this stalls.",
  RISK:         "Deployment risks are not owned by the customer — they are still yours until they are.",
};

// ─── HELPER ───────────────────────────────────────────────────────────────────

export function sectionFor(id: SectionId): Section {
  const s = SECTIONS.find((x) => x.id === id);
  if (!s) throw new Error(`Unknown section: ${id}`);
  return s;
}

/** Given answers and the set of fired follow-up IDs, build the full scored question list. */
export function buildScoredQuestions(
  firedFollowUpIds: string[],
): CalcQuestion[] {
  const followUpSet = new Set(firedFollowUpIds);
  return QUESTIONS.filter(
    (q) => !q.isFollowUp || followUpSet.has(q.id),
  );
}

/**
 * Evaluate which follow-ups should fire for a given answer.
 * Returns array of question IDs to insert.
 */
export function getFollowUps(parentId: string, points: number): string[] {
  const matchingRules = FOLLOW_UP_RULES.filter(
    (r) => r.parentId === parentId && r.triggerWhen.points.includes(points),
  );
  const allIds: string[] = [];
  for (const rule of matchingRules) {
    for (const id of rule.ask) {
      if (!allIds.includes(id)) allIds.push(id);
    }
  }
  return allIds;
}

/** Lookup a question by ID. */
export function questionById(id: string): CalcQuestion | undefined {
  return QUESTIONS.find((q) => q.id === id);
}
